// ============================================================================
// RÉFÉRENTIELS — les listes métier de l'enseigne.
//
// ⚠️ DOUZE ONGLETS SONT CONSTRUITS : « Fournisseurs & marques », « Secteurs », « Familles », « Sous-familles »,
// « Saisons », « Grilles de tailles », « Moyens de règlement », « Motifs de correction », « Unités de mesure »
// (lecture seule — la Console les sème), « Champs personnalisés », « Qualifications clients » et « Vocabulaire ». Les
// autres existent dans la maquette et sont annoncés ici, mais chacun DIT qu'il n'est pas repris
// plutôt que d'ouvrir sur du vide. Les masquer ferait croire que le référentiel se limite à un
// onglet ; les rendre cliquables sur une liste inexistante serait une option offerte non
// implémentée (§23.0).
//
// ⚠️ « MOTIFS DE CORRECTION » N'EST PAS UN AJOUT, C'EST UNE DETTE : l'écran des écarts envoyait déjà
// l'opérateur ici (« ajoutez-en dans Référentiels avant de justifier ») vers un onglet qui disait ne
// pas être repris. Une consigne qui désigne un endroit vide est exactement ce que le §23.0 refuse.
//
// ⚠️ POURQUOI CET ONGLET D'ABORD. Il porte le COEFFICIENT CORNER, la clause qui verrouille le prix
// d'achat des fiches en régime corner. Sans lui, la fiche article affiche « N/C » sur un régime
// entier, et l'assistant Gamme doit faire saisir un coefficient qui ne sert qu'à lui.
// ============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Badge, checkLabel, confirm, EmptyState, Icon, normalizeLabel, showToast, Tabs, Trans, useLatest } from '@orgazone/ui-web';
import { LABEL_COLORS, LABEL_COLOR_SWATCH } from '@orgazone/ui-tokens';
import {
  api, type ReferentialFacetListing, type ReferentialFamilyListing, type ClientQualification, type CustomField, type ReferentialJustification, type MeasurementUnitUsage, type ReferentialPaymentMethod, type ReferentialSuppliers, type ReferentialSupplier, type MarqueDuReferentiel, type ReferentialGrids } from '../api';
import { OngletGrilles } from './referentiels-grilles';
import { OngletChamps } from './referentiels-champs';
import { OngletReglements } from './referentiels-reglements';
import { OngletMotifs } from './referentiels-motifs';
import { MeasurementUnitsTab } from './referentiels-unites';
import { VocabularyTab } from './vocabulaire';
import { OrderedFacetTab, type OrderedFacetConfig } from './referential-ordered-list';
import { FamiliesTab } from './referential-families';
import { SubFamiliesTab } from './referential-sub-families';
import { annoncerCoefficient, ChampCoef, coefAffiche, coeffInput } from './champ-coefficient';
import { TermsBanner, TermsPanel, supplierInitials, supplierTint } from './supplier-terms-panel';
import { key, t, type Translatable } from '../i18n';

/** Les onglets de l'écran, dans l'ordre du relevé. */
const ONGLETS = [
  { k: 'sups', l: key('Fournisseurs & marques') },
  { k: 'sects', l: key('Secteurs') },
  { k: 'fams', l: key('Familles') },
  { k: 'sousfams', l: key('Sous-familles') },
  { k: 'sais', l: key('Saisons') },
  { k: 'grids', l: key('Grilles de tailles') },
  { k: 'champs', l: key('Champs personnalisés') },
  { k: 'tva', l: key('Taux de TVA') },
  { k: 'rem', l: key('Remises') },
  { k: 'motifs', l: key('Motifs de correction') },
  { k: 'regl', l: key('Moyens de règlement') },
  { k: 'qualifs', l: key('Qualifications clients') },
  { k: 'unites', l: key('Unités de mesure') },
  { k: 'frais', l: key('Catégories de frais') },
  { k: 'vocab', l: key('Vocabulaire') },
] as const;

/** Un onglet des Référentiels. */
export type ReferentialTab = (typeof ONGLETS)[number]['k'];

/** Les mots des Secteurs — une liste ordonnée, les nouveaux en fin (maquette, `sects`). */
const SECTORS: OrderedFacetConfig = {
  entity: 'sector', mono: false, addAtTop: false,
  title: key('Secteurs'),
  // ⚠️ « l'ordre descend en caisse » (maquette) N'EST PAS ÉCRIT : aucun écran de la caisse ne liste les secteurs, et le
  //    dire ferait promettre un effet qui n'existe pas. L'ordre range cette liste et descend avec elle.
  subtitle: key('Les rayons du magasin — portés par chaque article'),
  placeholder: key('Nouveau secteur — ex. Accessoires'),
  footnote: key('Semés selon le corps de métier à la création de la base (Console). L’ordre est une donnée : il descend aux caisses avec la liste.'),
  exists: key('Le secteur « {v} » existe déjà.'),
  used: key('utilisé — non supprimable'),
  inactive: key('désactivé'),
  deleteLabel: key('Supprimer le secteur « {v} »'),
  deleteTitle: key('Supprimer le secteur « {v} » ?'),
  deleteMessage: key('Aucun article ne l’utilise — suppression sans impact.'),
  created: key('Secteur ajouté · {v}'),
  deleted: key('Secteur supprimé · {v}'),
};

/** Les mots des Saisons — une liste ordonnée en chasse fixe, la nouvelle en tête (maquette, `sais`). */
const SEASONS: OrderedFacetConfig = {
  entity: 'reference_list', kind: 'season', mono: true, addAtTop: true,
  title: key('Saisons'),
  subtitle: key('Filtres, archivage, analyses — partout où l’on borne le temps'),
  placeholder: key('Nouvelle saison — ex. PE27'),
  footnote: key('La saison se pose sur chaque article (fiche, imports, assistant). L’archivage des articles s’appuie sur elle.'),
  exists: key('La saison « {v} » existe déjà.'),
  used: key('utilisée — non supprimable'),
  inactive: key('désactivée'),
  deleteLabel: key('Supprimer la saison « {v} »'),
  deleteTitle: key('Supprimer la saison « {v} » ?'),
  deleteMessage: key('Aucun article ne l’utilise — suppression sans impact.'),
  created: key('Saison ajoutée · {v}'),
  deleted: key('Saison supprimée · {v}'),
};

/** Les onglets, dans l'ordre de la barre — exportés pour leur banc. */
export const REFERENTIAL_TABS: readonly ReferentialTab[] = ONGLETS.map((tab) => tab.k);

/**
 * L'ONGLET QUE DEMANDE L'ADRESSE (`?tab=…`), ou le premier quand elle n'en nomme aucun de connu.
 * ⚠️ L'adresse PORTE l'onglet ouvert : un lien (« Gérer dans les référentiels » de la fiche article) y mène
 *    directement, et l'aide contextuelle sait de quel onglet elle parle.
 */
export function tabFromSearch(search: string): ReferentialTab {
  const asked = new URLSearchParams(search).get('tab');
  return REFERENTIAL_TABS.find((k) => k === asked) ?? 'sups';
}

export function Referentiels() {
  // `?tab=champs` : le bouton « Gérer dans les référentiels » de la fiche article arrive DIRECTEMENT
  // sur le bon onglet — atterrir sur les fournisseurs ferait chercher.
  const { search } = useLocation();
  const navigate = useNavigate();
  const onglet = tabFromSearch(search);
  // ⚠️ Changer d'onglet RÉÉCRIT l'adresse au lieu de l'empiler : « retour » ramène à l'écran précédent, pas à
  //    l'onglet d'avant.
  const setOnglet = (k: ReferentialTab) => navigate({ search: `?tab=${k}` }, { replace: true });
  /**
   * LE JEU VIT AU NIVEAU DE L’ÉCRAN, PAS DE L’ONGLET.
   * ⚠️ Monté dans l’onglet, il était relu à CHAQUE retour — et cette lecture coûte un balayage
   * complet du catalogue (l’article ne porte qu’un libellé de fournisseur, qu’il faut normaliser à
   * la volée, ce qu’aucun index ne sert). Aller voir « Familles » et revenir repayait la note.
   */
  const [jeu, setJeu] = useState<ReferentialSuppliers | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [grids, setGrids] = useState<ReferentialGrids | null>(null);
  const [echecGrilles, setEchecGrilles] = useState<string | null>(null);
  const [champs, setChamps] = useState<CustomField[] | null>(null);
  const [echecChamps, setEchecChamps] = useState<string | null>(null);
  const [reglements, setReglements] = useState<ReferentialPaymentMethod[] | null>(null);
  const [echecReglements, setEchecReglements] = useState<string | null>(null);
  const [motifs, setMotifs] = useState<ReferentialJustification[] | null>(null);
  const [echecMotifs, setEchecMotifs] = useState<string | null>(null);
  const [units, setUnits] = useState<MeasurementUnitUsage[] | null>(null);
  const [unitsFailure, setUnitsFailure] = useState<string | null>(null);
  const [sectors, setSectors] = useState<ReferentialFacetListing | null>(null);
  const [sectorsFailure, setSectorsFailure] = useState<string | null>(null);
  const [families, setFamilies] = useState<ReferentialFamilyListing | null>(null);
  const [familiesFailure, setFamiliesFailure] = useState<string | null>(null);
  const [subFamilies, setSubFamilies] = useState<ReferentialFacetListing | null>(null);
  const [subFamiliesFailure, setSubFamiliesFailure] = useState<string | null>(null);
  const [seasons, setSeasons] = useState<ReferentialFacetListing | null>(null);
  const [seasonsFailure, setSeasonsFailure] = useState<string | null>(null);

  // ⚠️ CHAQUE LISTE A SA PORTE « DERNIÈRE LECTURE LANCÉE » : une lecture d'ouverture lente qui reviendrait après celle qui
  //    suit une création ferait disparaître de l'écran la ligne qu'on vient d'ajouter. Une porte par liste — commune,
  //    elle ferait taire la lecture d'une liste au profit d'une autre.
  const latestSuppliers = useLatest();
  const latestGrids = useLatest();
  const latestFields = useLatest();
  const latestReasons = useLatest();
  const latestUnits = useLatest();
  const latestPayments = useLatest();
  const latestSectors = useLatest();
  const latestFamilies = useLatest();
  const latestSubFamilies = useLatest();
  const latestSeasons = useLatest();

  const reload = useCallback(() => {
    const isLatest = latestSuppliers();
    api.referentielSuppliers()
      .then((r) => { if (!isLatest()) return; setJeu(r); setFailure(null); })
      // ⚠️ L'ÉCHEC SE DIT : une liste vide se lirait « ce magasin n'a pas de fournisseur », et
      // quelqu'un les recréerait un par un par-dessus ceux qui existent.
      .catch((e) => { if (!isLatest()) return; setJeu(null); setFailure(e instanceof Error ? e.message : String(e)); });
  }, [latestSuppliers]);

  const relireGrilles = useCallback(() => {
    const isLatest = latestGrids();
    api.referentielSizeGrids()
      .then((r) => { if (!isLatest()) return; setGrids(r); setEchecGrilles(null); })
      .catch((e) => { if (!isLatest()) return; setGrids(null); setEchecGrilles(e instanceof Error ? e.message : String(e)); });
  }, [latestGrids]);

  const relireChamps = useCallback(() => {
    const isLatest = latestFields();
    api.customFields()
      .then((r) => { if (!isLatest()) return; setChamps(r.rows); setEchecChamps(null); })
      .catch((e) => { if (!isLatest()) return; setChamps(null); setEchecChamps(e instanceof Error ? e.message : String(e)); });
  }, [latestFields]);

  const relireMotifs = useCallback(() => {
    const isLatest = latestReasons();
    api.referentielJustifications()
      // ⚠️ L'ÉCHEC SE DIT : une liste vide se lirait « ce magasin n'a aucun motif », et quelqu'un en
      //    recréerait par-dessus ceux qui existent — avec le même code, donc la même ligne.
      .then((r) => { if (!isLatest()) return; setMotifs(r.rows); setEchecMotifs(null); })
      .catch((e) => { if (!isLatest()) return; setMotifs(null); setEchecMotifs(e instanceof Error ? e.message : String(e)); });
  }, [latestReasons]);

  const readUnits = useCallback(() => {
    const isLatest = latestUnits();
    api.referentielMeasurementUnits()
      // ⚠️ L'ÉCHEC SE DIT : une liste vide se lirait « ce magasin n'a aucune unité ».
      .then((r) => { if (!isLatest()) return; setUnits(r.rows); setUnitsFailure(null); })
      .catch((e) => { if (!isLatest()) return; setUnits(null); setUnitsFailure(e instanceof Error ? e.message : String(e)); });
  }, [latestUnits]);

  const relireReglements = useCallback(() => {
    const isLatest = latestPayments();
    api.referentielPaymentMethods()
      // ⚠️ L'ÉCHEC SE DIT : une liste vide se lirait « ce magasin n'encaisse rien », et quelqu'un
      //    recréerait des moyens par-dessus ceux qui existent — avec le même code, donc la même ligne.
      .then((r) => { if (!isLatest()) return; setReglements(r.rows); setEchecReglements(null); })
      .catch((e) => { if (!isLatest()) return; setReglements(null); setEchecReglements(e instanceof Error ? e.message : String(e)); });
  }, [latestPayments]);

  // ⚠️ L'ÉCHEC SE DIT, pour les quatre facettes comme pour les autres listes : une liste vide se lirait « ce magasin n'a
  //    aucune famille », et quelqu'un les recréerait par-dessus celles qui existent.
  const readSectors = useCallback(() => {
    const isLatest = latestSectors();
    api.referentielSectors()
      .then((r) => { if (!isLatest()) return; setSectors(r); setSectorsFailure(null); })
      .catch((e) => { if (!isLatest()) return; setSectors(null); setSectorsFailure(e instanceof Error ? e.message : String(e)); });
  }, [latestSectors]);
  const readFamilies = useCallback(() => {
    const isLatest = latestFamilies();
    api.referentielFamilies()
      .then((r) => { if (!isLatest()) return; setFamilies(r); setFamiliesFailure(null); })
      .catch((e) => { if (!isLatest()) return; setFamilies(null); setFamiliesFailure(e instanceof Error ? e.message : String(e)); });
  }, [latestFamilies]);
  const readSubFamilies = useCallback(() => {
    const isLatest = latestSubFamilies();
    api.referentielSubFamilies()
      .then((r) => { if (!isLatest()) return; setSubFamilies(r); setSubFamiliesFailure(null); })
      .catch((e) => { if (!isLatest()) return; setSubFamilies(null); setSubFamiliesFailure(e instanceof Error ? e.message : String(e)); });
  }, [latestSubFamilies]);
  const readSeasons = useCallback(() => {
    const isLatest = latestSeasons();
    api.referentielSeasons()
      .then((r) => { if (!isLatest()) return; setSeasons(r); setSeasonsFailure(null); })
      .catch((e) => { if (!isLatest()) return; setSeasons(null); setSeasonsFailure(e instanceof Error ? e.message : String(e)); });
  }, [latestSeasons]);

  // ⚠️ CHAQUE ONGLET LIT LE SIEN, à sa première ouverture seulement : tout charger d’emblée ferait
  // payer onze lectures à qui n’en regarde qu’une. Le jeu vit ensuite au niveau de l’écran, donc
  // revenir sur l’onglet ne repaie rien.
  useEffect(reload, [reload]);
  useEffect(() => { if (onglet === 'grids' && grids === null && echecGrilles === null) relireGrilles(); },
    [onglet, grids, echecGrilles, relireGrilles]);
  useEffect(() => { if (onglet === 'champs' && champs === null && echecChamps === null) relireChamps(); },
    [onglet, champs, echecChamps, relireChamps]);
  useEffect(() => { if (onglet === 'regl' && reglements === null && echecReglements === null) relireReglements(); },
    [onglet, reglements, echecReglements, relireReglements]);
  useEffect(() => { if (onglet === 'motifs' && motifs === null && echecMotifs === null) relireMotifs(); },
    [onglet, motifs, echecMotifs, relireMotifs]);
  useEffect(() => { if (onglet === 'unites' && units === null && unitsFailure === null) readUnits(); },
    [onglet, units, unitsFailure, readUnits]);
  useEffect(() => { if (onglet === 'sects' && sectors === null && sectorsFailure === null) readSectors(); },
    [onglet, sectors, sectorsFailure, readSectors]);
  useEffect(() => { if (onglet === 'fams' && families === null && familiesFailure === null) readFamilies(); },
    [onglet, families, familiesFailure, readFamilies]);
  useEffect(() => { if (onglet === 'sousfams' && subFamilies === null && subFamiliesFailure === null) readSubFamilies(); },
    [onglet, subFamilies, subFamiliesFailure, readSubFamilies]);
  useEffect(() => { if (onglet === 'sais' && seasons === null && seasonsFailure === null) readSeasons(); },
    [onglet, seasons, seasonsFailure, readSeasons]);

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 18, flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{
            margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)',
          }}>{t('Référentiels')}</h1>
          <p style={{ margin: '3px 0 0', fontSize: '13.5px', color: 'var(--ink-500)' }}>
            
            {t('Les listes métier de l’enseigne — elles classent le catalogue, encadrent la caisse et descendent sur toutes les apps.')}
          </p>
        </div>
      </div>

      {/* ⚠️ LA BARRE VIENT DU SOCLE, ELLE NE SE REFAIT PAS À LA MAIN. La mienne reposait
          `overflow-x: auto` SEUL : la spécification CSS calcule alors l'autre axe en `auto` aussi, et
          le `margin-bottom: -1` des onglets (le trait d'actif doit chevaucher le filet) dépassait la
          boîte d'un pixel — un ASCENSEUR VERTICAL pour un pixel. `Tabs` porte ce correctif, sa
          tabulation glissante et ses flèches ; le cran `md` est le relevé exact de cette barre. */}
      <Tabs
        items={ONGLETS.map((tab) => ({ id: tab.k, label: t(tab.l) }))}
        value={onglet}
        onChange={setOnglet}
        panelId="bo-referentiels"
        label={t('Listes métier de l’enseigne')}
        size="md"
      />

      <div id="bo-referentiels" role="tabpanel" aria-labelledby={`bo-referentiels-tab-${onglet}`}>
        {onglet === 'sups' ? <OngletFournisseurs jeu={jeu} failure={failure} reload={reload} />
          : onglet === 'sects' ? <OrderedFacetTab cfg={SECTORS} listing={sectors} failure={sectorsFailure} reload={readSectors} />
          : onglet === 'fams' ? <FamiliesTab listing={families} failure={familiesFailure} reload={readFamilies} />
          : onglet === 'sousfams' ? <SubFamiliesTab listing={subFamilies} failure={subFamiliesFailure} reload={readSubFamilies} />
          : onglet === 'sais' ? <OrderedFacetTab cfg={SEASONS} listing={seasons} failure={seasonsFailure} reload={readSeasons} />
          : onglet === 'grids' ? <OngletGrilles jeu={grids} failure={echecGrilles} reload={relireGrilles} />
            : onglet === 'regl' ? <OngletReglements jeu={reglements} failure={echecReglements} reload={relireReglements} />
            : onglet === 'motifs' ? <OngletMotifs jeu={motifs} failure={echecMotifs} reload={relireMotifs} />
            : onglet === 'unites' ? <MeasurementUnitsTab units={units} failure={unitsFailure} />
            : onglet === 'champs' ? <OngletChamps jeu={champs} failure={echecChamps} reload={relireChamps} />
              : onglet === 'qualifs' ? <OngletQualifications />
              : onglet === 'vocab' ? <VocabularyTab />
              : <PasEncoreRepris nom={t(ONGLETS.find((tab) => tab.k === onglet)!.l)} />}
      </div>
    </div>
  );
}

/**
 * CE QUI N'EST PAS CONSTRUIT LE DIT.
 * ⚠️ Ni une liste vide, ni un « chargement » perpétuel : les deux se lisent « ce magasin n'a rien »,
 * et quelqu'un finirait par ressaisir un référentiel qui existe déjà ailleurs.
 */
function PasEncoreRepris({ nom }: { nom: string }) {
  return (
    <div style={{
      padding: '28px 22px', borderRadius: 'var(--r-md)', border: '1px dashed var(--ink-300)',
      background: 'var(--ink-50)', textAlign: 'center',
    }}>
      <div style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--ink-900)' }}>
        {t('« {nom} » n’est pas encore repris', { nom })}</div>
      <p style={{
        margin: '6px auto 0', maxWidth: 460, fontSize: '12.5px', color: 'var(--ink-500)', lineHeight: 1.55,
      }}>
        
        {t('L’onglet est prévu, sa liste n’est pas branchée. Rien n’a été perdu : les valeurs existantes restent visibles dans les filtres du catalogue et sur les fiches articles.')}
      </p>
    </div>
  );
}

function OngletFournisseurs({ jeu, failure, reload }: {
  jeu: ReferentialSuppliers | null;
  failure: string | null;
  reload: () => void;
}) {
  const [composeur, setComposeur] = useState<string | null>(null);
  // ⚠️ UN SEUL PANNEAU OUVERT À LA FOIS (le dessin) : deux fiches dépliées côte à côte se confondent.
  const [openSupplier, setOpenSupplier] = useState<string | null>(null);

  /**
   * Écrit une entrée de référentiel, puis RELIT.
   * ⚠️ On ne recopie pas la valeur dans l'état local en supposant que le serveur a suivi : c'est le
   * producteur qui décide (normalisation, refus, valeur déjà prise). Afficher notre supposition
   * ferait croire à un enregistrement que le journal n'a peut-être pas accepté.
   */
  const write = useCallback(async (
    entite: 'supplier' | 'brand' | 'supplier_terms',
    entry: Record<string, string | number | null>,
    intention: string,
  ) => {
    try {
      await api.createReferentielEntry(entite, entry, intention);
      reload();
      return null;
    } catch (e) {
      // ⚠️ LE MOTIF EST RENDU, pas seulement affiché : un champ qui garde la valeur refusée doit pouvoir
      //    dire « Non enregistré », sinon la relecture ne change rien et la saisie passe pour enregistrée.
      const reason = e instanceof Error ? e.message : String(e);
      showToast(reason, 'err');
      reload();
      return reason;
    }
  }, [reload]);

  /**
   * SUPPRIMER une valeur de référentiel — le serveur refuse si elle est encore portée.
   * ⚠️ L’écran ne décide PAS de la faisabilité : il propose le geste quand son compte affiche zéro,
   * et c’est le producteur qui mesure. Un refus revient avec son motif réel, qui est affiché tel quel.
   */
  const remove = useCallback(async (
    entite: 'supplier' | 'brand' | 'supplier_terms',
    entry: Record<string, string | number | null>,
    intention: string,
  ) => {
    try {
      await api.deleteReferentielEntry(entite, entry, intention);
      reload();
      return null;
    } catch (e) {
      const reason = e instanceof Error ? e.message : String(e);
      showToast(reason, 'err');
      reload();
      return reason;
    }
  }, [reload]);

  if (failure !== null) {
    return (
      <div style={{
        padding: '13px 15px', borderRadius: 'var(--r-sm)', background: 'var(--rose-bg)',
        border: '1px solid var(--rose-fg)', color: 'var(--rose-fg)', fontSize: '12.5px', lineHeight: 1.5,
      }} role="alert">
        {t('Le référentiel n’a pas pu être lu : {failure}', { failure })}
      </div>
    );
  }
  if (jeu === null) {
    return <div style={{ fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Lecture du référentiel…')}</div>;
  }

  const nMarques = jeu.rows.reduce((n, s) => n + s.brands.length, 0) + jeu.orphanBrands.length;
  /**
   * TOUTES les marques du magasin, avec le fournisseur qui les porte AUJOURD’HUI.
   * ⚠️ La liste doit être COMPLÈTE : comparée à un sous-ensemble, une marque existante serait
   * déclarée neuve, et c’est exactement ainsi qu’on crée un doublon en croyant bien faire.
   */
  const marques = [
    ...jeu.rows.flatMap((f) => f.brands.map((b) => ({ label: b.label, code: b.code, chez: f.label }))),
    ...jeu.orphanBrands.map((b) => ({ label: b.label, code: b.code, chez: null })),
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '13.5px', color: 'var(--ink-600)' }}>
          <Trans k='<1>{fournisseurs}</1> · <2>{marques}</2>'
            vars={{ fournisseurs: t('{n} fournisseur', { n: jeu.rows.length }), marques: t('{n} marque', { n: nMarques }) }}
            parts={[(x) => <strong style={{ color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{x}</strong>]} /></span>
        <div style={{ flex: 1 }} />
        <button
          type="button" onClick={() => setComposeur('')}
          style={{
            height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)',
            background: 'var(--teal-500)', color: '#fff', fontSize: '13.5px', fontWeight: 700,
            display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer',
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.2" strokeLinecap="round" aria-hidden><path d="M12 5v14M5 12h14" /></svg>
          
          {t('Nouveau fournisseur')}
        </button>
      </div>

      {composeur !== null && (
        <Composeur
          valeur={composeur}
          onChange={setComposeur}
          doublon={jeu.rows.some((s) => s.label.trim().toLowerCase() === composeur.trim().toLowerCase())}
          onCancel={() => setComposeur(null)}
          onCreate={async () => {
            const nom = composeur.trim();
            if (nom === '') return;
            // ⚠️ UN REFUS GARDE LA BANDE OUVERTE, le nom tapé dedans : la refermer laisserait croire la création faite.
            if ((await write('supplier', { code: nom, label: nom }, `ref:supplier:${nom}`)) === null) setComposeur(null);
          }}
        />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {jeu.rows.map((s) => (
          <CarteFournisseur key={s.code} s={s} marques={marques} write={write} remove={remove}
            open={openSupplier === s.code} onToggle={() => setOpenSupplier((o) => (o === s.code ? null : s.code))} />
        ))}
        {jeu.orphanBrands.length > 0 && (
          <CarteOrphelines marques={jeu.orphanBrands} />
        )}
      </div>

      {jeu.truncated && (
        <div style={{ marginTop: 12, fontSize: 'var(--t-dense)', color: 'var(--amber-fg)', fontWeight: 600 }}>
          {t('Liste bornée à {n} fournisseur — d’autres existent au-delà.', { n: jeu.limit })}</div>
      )}

      <div style={{ marginTop: 12, fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
        <Trans k='Un fournisseur sans marque déclarée vend sous son propre nom — fournisseur et marque ne font qu’un. Les marques déclarées ici alimentent le champ « Marque » des fiches articles, filtré par fournisseur. Le <1>coef corner</1> du fournisseur descend à toutes ses marques ; la case violette d’une marque le surcharge — c’est lui qui verrouille le coefficient des fiches en régime corner. Le <2>libellé</2> d’une marque se corrige en cliquant dessus ; une marque qu’aucun article ne porte peut être retirée du référentiel. En revanche son <3>code</3> porte son identité et ne change pas : une faute de frappe dans le code se corrige en créant la bonne marque, puis en retirant l’ancienne.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></div>
    </div>
  );
}

/** La bande de création d'un fournisseur — elle s'ouvre en place, sans quitter la liste. */
function Composeur({ valeur, onChange, doublon, onCreate, onCancel }: {
  valeur: string;
  onChange: (v: string) => void;
  doublon: boolean;
  onCreate: () => void;
  onCancel: () => void;
}) {
  const ready = valeur.trim() !== '' && !doublon;
  return (
    <div style={{
      border: '1px solid var(--teal-300)', background: 'var(--teal-100)', borderRadius: 'var(--r-md)',
      padding: '14px 16px', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-end',
      flexWrap: 'wrap',
    }}>
      <label style={{ flex: 1, minWidth: 220 }}>
        <span style={{
          display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--teal-800)', marginBottom: 5,
        }}>{t('Nom du fournisseur')}</span>
        <input
          value={valeur} onChange={(e) => onChange(e.target.value)} autoFocus
          placeholder={t('Bestseller, PVH, Zag Bijoux…')}
          onKeyDown={(e) => { if (e.key === 'Enter' && ready) onCreate(); }}
          style={{
            width: '100%', height: 40, padding: '0 12px', boxSizing: 'border-box',
            border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
            color: 'var(--ink-900)', fontSize: '13.5px', outline: 'none',
          }}
        />
      </label>
      <button
        // ⚠️ LE CLIC REFUSE CE QUE LA TOUCHE ENTRÉE REFUSE : un doublon, que le bouton annonce inactif.
        type="button" onClick={() => { if (ready) onCreate(); }} aria-disabled={!ready || undefined}
        style={{
          height: 40, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)',
          background: ready ? 'var(--teal-500)' : 'var(--ink-200)',
          color: ready ? '#fff' : 'var(--ink-400)', fontSize: '13.5px', fontWeight: 700,
          cursor: ready ? 'pointer' : 'not-allowed',
        }}
      >{t('Créer')}</button>
      <button
        type="button" onClick={onCancel}
        style={{
          height: 40, padding: '0 13px', border: '1px solid var(--teal-300)',
          borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)',
          fontSize: '13.5px', fontWeight: 600, cursor: 'pointer',
        }}
      >{t('Annuler')}</button>
      {doublon && (
        <span style={{ width: '100%', fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--amber-fg)' }}>
          
          {t('Ce fournisseur existe déjà.')}
        </span>
      )}
    </div>
  );
}

/** Écrit (ou retire) une entrée de référentiel — rend le motif du refus, `null` si le serveur a accepté. */
type Ecrire = (
  entite: 'supplier' | 'brand' | 'supplier_terms',
  entry: Record<string, string | number | null>,
  key: string,
) => Promise<string | null>;

function CarteFournisseur({ s, marques, write, remove, open, onToggle }: {
  s: ReferentialSupplier;
  marques: readonly { label: string; code: string; chez: string | null }[];
  write: Ecrire;
  remove: Ecrire;
  open: boolean;
  onToggle: () => void;
}) {
  const tint = supplierTint(s.code);

  return (
    <div style={{
      background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)',
      padding: '15px 18px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, flexWrap: 'wrap' }}>
        {/* ⚠️ DEUX INITIALES SUR UNE TEINTE (le dessin) : la tuile se lit d'un coup d'œil dans
            une longue liste, ce que l'icône commune à toutes les cartes ne permettait pas. La teinte vient
            du CODE, pas du rang, pour ne pas changer quand on crée un fournisseur. */}
        <span style={{
          width: 38, height: 38, borderRadius: 'var(--r-sm)', background: tint.bg, color: tint.fg,
          flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '13px', fontWeight: 800, letterSpacing: '.02em',
        }} aria-hidden>{supplierInitials(s.label)}</span>
        <span style={{ flex: 1, minWidth: 140 }}>
          <span style={{
            display: 'block', fontSize: '14.5px', fontWeight: 700, letterSpacing: '-0.015em',
            color: 'var(--ink-900)',
          }}>{s.label}</span>
          <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 1 }}>
            {t('{n} article', { n: s.models })}
          </span>
        </span>
        {/* ⚠️ « Un fournisseur sans marque déclarée vend sous son propre nom » — le dire évite de
            prendre une liste vide pour un oubli de saisie. */}
        {s.brands.length === 0 && (
          <span style={{
            height: 22, padding: '0 9px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)',
            color: 'var(--ink-600)', fontSize: 'var(--t-meta)', fontWeight: 600, display: 'inline-flex',
            alignItems: 'center', flexShrink: 0,
          }}>{t('fournisseur = marque')}</span>
        )}
      </div>

      <TermsBanner s={s} open={open} onToggle={onToggle} />
      {open && (
        <TermsPanel
          s={s}
          write={(fields, intention) => write('supplier_terms', { supplierCode: s.code, ...fields }, intention)}
          onCoef={(bp) => write('supplier', { code: s.code, coefficientBp: bp }, `coef:supplier:${s.code}`).then((refus) => {
            if (refus === null) annoncerCoefficient(s.label, bp);
            return refus === null;
          })}
        />
      )}

      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 12, alignItems: 'center' }}>
        {s.brands.map((b) => (
          <PastilleMarque key={b.code} b={b} heritage={s.coefficientBp} supplier={s}
            write={write} remove={remove} />
        ))}
        <AjoutDeMarque s={s} marques={marques} write={write} />
      </div>
    </div>
  );
}

/**
 * AJOUTER UNE MARQUE À UN FOURNISSEUR — créer la sienne, ou RATTACHER une marque qui existe déjà.
 *
 * ⚠️ CE CONTRÔLE NE POUVAIT PAS ÊTRE UN SIMPLE CHAMP DE CRÉATION, et c'est le retour utilisateur qui
 * l'a montré : l'identité d'une marque est son code normalisé. Taper « Jack & Jones » chez un autre
 * fournisseur ne créait donc PAS un doublon — le producteur retrouvait la ligne existante et la
 * DÉPLAÇAIT, en silence, hors du fournisseur qui la portait. Le geste le plus anodin de l'écran était
 * aussi le plus destructeur, et rien ne le disait.
 *
 * ⚠️ LA RECONNAISSANCE VIENT DU SOCLE (`verifierLibelle`) : « JACK & JONES », « jack et jones » et
 * « Jones Jack » désignent la même marque. Une comparaison à l'égalité stricte n'en verrait aucune, et
 * le déplacement silencieux serait de retour au premier accent.
 *
 * ⚠️ TROIS ISSUES, JAMAIS UNE SEULE : créer (rien d'approchant), rattacher (elle existe — et l'écran
 * DIT d'où elle vient), ou trancher soi-même quand ça ressemble sans être identique.
 */
function AjoutDeMarque({ s, marques, write }: {
  s: ReferentialSupplier;
  /** Toutes les marques du magasin, avec le fournisseur qui les porte aujourd'hui (`null` = aucune). */
  marques: readonly { label: string; code: string; chez: string | null }[];
  write: Ecrire;
}) {
  const [saisie, setSaisie] = useState('');
  const verdict = checkLabel(saisie, marques.map((m) => m.label));
  const connue = verdict.etat === 'exact' || verdict.etat === 'proche'
    ? marques.find((m) => m.label === verdict.candidat) ?? null
    : null;
  // Déjà chez CE fournisseur : il n'y a rien à faire, et le dire vaut mieux qu'un bouton qui ne fera rien.
  const dejaIci = connue !== null && connue.chez === s.label;

  /**
   * Les marques que la saisie évoque, et qui ne sont PAS déjà chez ce fournisseur.
   * ⚠️ La comparaison se fait sur la forme PLIÉE (`normaliserLibelle`) : « jack » doit trouver
   * « Jack & Jones », sinon la suggestion ne sert que ceux qui tapent déjà juste.
   * ⚠️ Borné à six : une rangée qui déborde ne se lit plus, et l’exact a déjà sa propre ligne.
   */
  const suggestions = useMemo(() => {
    const q = normalizeLabel(saisie).toLowerCase();
    if (q.length < 2) return [];
    return marques
      .filter((m) => m.chez !== s.label
        && normalizeLabel(m.label).toLowerCase().includes(q)
        && m.label !== (verdict.etat === 'exact' ? verdict.candidat : null))
      .slice(0, 6);
  }, [marques, saisie, s.label, verdict]);

  /** Rattache une marque existante — en DEMANDANT quand le geste la retire à quelqu'un d'autre. */
  const rattacher = useCallback(async (m: { label: string; code: string; chez: string | null }) => {
    if (m.chez !== null && m.chez !== s.label) {
      const accord = await confirm({
        title: t('Déplacer « {label} » de {chez} vers {label2} ?', { label: m.label, chez: m.chez, label2: s.label }),
        message: t('Une marque n’appartient qu’à un fournisseur. Elle quittera {chez} — les articles qui la portent gardent leur étiquette, mais hériteront du coef corner de {label}.', { chez: m.chez, label: s.label }),
        confirmLabel: t('Déplacer'),
      });
      if (!accord) return;
    }
    await write('brand', { code: m.code, supplierCode: s.code }, `attach:brand:${m.code}`);
    setSaisie('');
  }, [write, s.code, s.label]);

  const create = useCallback(async () => {
    const nom = saisie.trim();
    if (nom === '') return;
    // ⚠️ `supplierCode` PORTE LE RATTACHEMENT : sans lui la marque naîtrait orpheline, et le geste
    // « ajouter une marque À Bestseller » n'aurait rien rattaché.
    await write('brand', { code: nom, label: nom, supplierCode: s.code }, `brand:${s.code}:${nom}`);
    setSaisie('');
  }, [write, saisie, s.code]);

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
        <input
          value={saisie} onChange={(e) => setSaisie(e.target.value)}
          placeholder={t('Nouvelle marque…')}
          aria-label={t('Ajouter une marque à {label}', { label: s.label })}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            if (dejaIci) return;
            if (connue !== null && verdict.etat === 'exact') void rattacher(connue);
            else if (verdict.etat === 'neuf') void create();
          }}
          style={{
            width: 160, height: 32, padding: '0 11px', boxSizing: 'border-box',
            border: '1px dashed var(--ink-300)', borderRadius: 'var(--r-pill)',
            background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '12.5px', outline: 'none',
          }}
        />
        {verdict.etat === 'exact' && connue !== null ? (
          <BoutonPill onClick={() => void rattacher(connue)} disabled={dejaIci}>{t('Rattacher')}</BoutonPill>
        ) : (
          <BoutonPill onClick={() => void create()} disabled={verdict.etat === 'vide'}>{t('Ajouter')}</BoutonPill>
        )}
      </span>

      {/* ⚠️ CE QUE LE BOUTON VA FAIRE, AVANT DE LE FAIRE. « Rattacher » sans dire d'où la marque vient
          laisserait découvrir après coup qu'on l'a prise à un autre fournisseur. */}
      {verdict.etat === 'exact' && connue !== null && (
        <span style={{ fontSize: '11.5px', color: dejaIci ? 'var(--ink-500)' : 'var(--amber-fg)', fontWeight: 600 }}>
          {dejaIci
            ? t('« {label} » est déjà une marque de {label2}.', { label: connue.label, label2: s.label })
            : connue.chez === null
              ? t('« {label} » existe sans fournisseur — elle sera rattachée ici.', { label: connue.label })
              : t('« {label} » appartient à {chez} — la rattacher ici l’en retirera.', { label: connue.label, chez: connue.chez })}
        </span>
      )}

      {/* ⚠️ LES SUGGESTIONS SONT LE CŒUR DU CONTRÔLE, pas un confort. Sans elles il faut CONNAÎTRE
          le nom exact de la marque à rattacher — et une marque qu’on ne se rappelle pas se retape
          approximativement, ce qui en crée une seconde. Elles nomment aussi le fournisseur actuel,
          pour qu’on voie ce qu’on s’apprête à déplacer AVANT de cliquer. */}
      {suggestions.length > 0 && (
        <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {suggestions.map((m) => (
            <button
              key={m.code} type="button" onClick={() => void rattacher(m)}
              style={{
                height: 26, padding: '0 10px', border: '1px dashed var(--ink-300)',
                borderRadius: 'var(--r-pill)', background: 'var(--ink-50)', color: 'var(--ink-700)',
                fontSize: 'var(--t-dense)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
              }}
            >
              {m.label}
              <span style={{ color: 'var(--ink-400)', fontWeight: 500 }}>
                {' · '}{m.chez === null ? t('sans fournisseur') : t('chez {chez}', { chez: m.chez })}
              </span>
            </button>
          ))}
        </span>
      )}

      {/* Ressemblant sans être identique : les DEUX issues restent ouvertes, c'est à l'humain de dire
          si « Jack&Jones » et « Jack & Jones » sont la même maison. */}
      {verdict.etat === 'proche' && connue !== null && (
        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11.5px', color: 'var(--amber-fg)', fontWeight: 600 }}>
            {connue.chez !== null
              ? t('Ressemble à « {label} » — {pourcent} % (chez {chez})', { label: connue.label, pourcent: verdict.pourcent, chez: connue.chez })
              : t('Ressemble à « {label} » — {pourcent} %', { label: connue.label, pourcent: verdict.pourcent })}
          </span>
          <BoutonPill onClick={() => void rattacher(connue)}>{t('Rattacher « {label} »', { label: connue.label })}</BoutonPill>
          <BoutonPill onClick={() => void create()}>{t('Créer quand même')}</BoutonPill>
        </span>
      )}
    </span>
  );
}

/** Le bouton en pastille de la rangée des marques — 32 px, contour teal, comme au relevé. */
function BoutonPill({ onClick, disabled, children }: {
  onClick: () => void; disabled?: boolean; children: React.ReactNode;
}) {
  return (
    <button
      type="button" onClick={onClick} disabled={disabled}
      style={{
        height: 32, padding: '0 12px', border: '1px solid var(--teal-400)',
        borderRadius: 'var(--r-pill)', background: 'var(--paper)', color: 'var(--teal-800)',
        fontSize: 'var(--t-dense)', fontWeight: 700, whiteSpace: 'nowrap',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
      }}
    >{children}</button>
  );
}

/** Une marque du fournisseur — son nom, sa clause propre, ce qu'elle porte, et de quoi la détacher. */
function PastilleMarque({ b, heritage, supplier, write, remove }: {
  b: MarqueDuReferentiel;
  heritage: number | null;
  supplier: ReferentialSupplier;
  write: Ecrire;
  remove: Ecrire;
}) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 7, height: 30, padding: '0 6px 0 11px',
      border: '1px solid var(--violet-bg)', borderRadius: 'var(--r-pill)', background: 'var(--violet-bg)',
    }}>
      {/* ⚠️ LE LIBELLÉ SE CORRIGE SUR PLACE. Une marque saisie avec une faute n'était ni modifiable ni
          supprimable : elle restait au référentiel et s'offrait dans le champ « Marque » de toutes les
          fiches (retour utilisateur 2026-08-21). C'est le LIBELLÉ qui s'édite ici — pas le code, qui
          porte l'identité de la ligne (voir la note de bas d'onglet). */}
      <ChampTexte
        valeur={b.label}
        heading={t('Corriger le libellé de la marque — le code, lui, porte son identité')}
        nom={t('Libellé de la marque {v}', { v: b.label })}
        onValidate={(v) => {
          const nom = v.trim();
          // Un libellé vidé ne s'écrit pas — la case reprend le libellé enregistré.
          if (nom === '') return Promise.resolve(false);
          if (nom === b.label) return;
          return write('brand', { code: b.code, label: nom }, `label:brand:${b.code}`).then((refus) => {
            // ⚠️ LE SUCCÈS SE DIT, COMME L'ÉCHEC (Forme X) : le libellé s'écrit à la sortie du champ, sans bouton.
            if (refus === null) showToast(t('Marque renommée : « {v} ».', { v: nom }), 'ok');
            return refus === null;
          });
        }}
      />
      <ChampCoef
        valeur={coefAffiche(b.coefficientBp)}
        // ⚠️ LE REPÈRE EST LA VALEUR HÉRITÉE, en filigrane : une case vide sans repère laisserait croire
        // qu'aucun coefficient ne s'applique, alors que celui du fournisseur commande.
        placeholder={coefAffiche(heritage) || '—'}
        heading={t('Coef corner de la marque — vide : hérite du fournisseur')}
        nom={t('Coefficient de {v}', { v: b.label })}
        largeur={46} hauteur={22} taille="11px" couleur="var(--violet-fg)"
        onValidate={(v) => {
          const bp = coeffInput(v);
          if (bp === undefined) { showToast(t('Coefficient attendu, par exemple « 2,50 ».'), 'warn'); return Promise.resolve(false); }
          if (bp === b.coefficientBp) return;
          return write('brand', { code: b.code, coefficientBp: bp }, `coef:brand:${b.code}`).then((refus) => {
            if (refus === null) annoncerCoefficient(b.label, bp);
            return refus === null;
          });
        }}
      />
      {b.models > 0 && (
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: '10.5px', color: 'var(--ink-400)', whiteSpace: 'nowrap',
        }}>{t('{n} article', { n: b.models })}</span>
      )}
      {/* ⚠️ SUPPRESSION RÉELLE, et seulement quand AUCUN article ne la porte. L'écran PROPOSE le geste
          sur son compte à zéro ; c'est le producteur qui MESURE, dans la transaction. Son compte à lui
          a une seconde de retard, et une seconde suffit à tombstoner sur toute la flotte une marque
          qu'un import vient d'utiliser. */}
      {b.models === 0 && (
        <button
          type="button" aria-label={t('Supprimer la marque {label}', { label: b.label })}
          title={t('Supprimer la marque — aucune fiche ne la porte')}
          onClick={async () => {
            const accord = await confirm({
              title: t('Supprimer la marque « {label} » ?', { label: b.label }),
              message: t('Aucun article ne la porte. Elle disparaîtra du référentiel ET des caisses. Le serveur vérifie ce compte au moment de supprimer : s’il a changé entre-temps, il refusera et le dira.'),
              confirmLabel: t('Supprimer'),
            });
            if (accord) await remove('brand', { code: b.code }, `del:brand:${b.code}`);
          }}
          style={{
            width: 20, height: 20, border: 'none', borderRadius: 'var(--r-pill)', background: 'transparent',
            color: 'var(--ink-400)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 0, cursor: 'pointer',
          }}
        >
          <Icon name="ban" size={12} stroke={2} />
        </button>
      )}
      <button
        type="button" aria-label={t('Retirer la marque {label}', { label: b.label })}
        onClick={async () => {
          const accord = await confirm({
            title: t('Retirer « {label} » des marques de {label2} ?', { label: b.label, label2: supplier.label }),
            message: t('Les articles qui la portent gardent leur étiquette actuelle.'),
            confirmLabel: t('Retirer'),
            cancelLabel: t('Garder'),
          });
          // ⚠️ ON DÉTACHE, ON NE SUPPRIME PAS : la marque reste sur les étiquettes des articles qui la
          // portent. La supprimer les laisserait pointer dans le vide.
          if (accord) await write('brand', { code: b.code, supplierCode: null }, `detach:brand:${b.code}`);
        }}
        style={{
          width: 20, height: 20, border: 'none', borderRadius: 'var(--r-pill)', background: 'transparent',
          color: 'var(--ink-400)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 0, cursor: 'pointer',
        }}
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2.4" strokeLinecap="round" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>
      </button>
    </span>
  );
}

/**
 * UN LIBELLÉ QUI SE CORRIGE SUR PLACE — même règle que la case de coefficient : on n'écrit qu'à la
 * SORTIE du champ. Écrire à la frappe enverrait au journal chaque état intermédiaire d'une correction.
 * ⚠️ Il se DIMENSIONNE sur son contenu : une largeur fixe couperait « Jack & Jones » ou laisserait un
 * grand vide après « Villa », dans une rangée de pastilles qui se lisent d'un coup d'œil.
 */
function ChampTexte({ valeur, heading, nom, onValidate, encadre }: {
  valeur: string; heading: string;
  /** Le nom du champ quand il se répète dans une liste — la bulle est la même sur chaque ligne, le nom dit LAQUELLE. */
  nom?: string;
  /** Ce que la sortie du champ décide — une promesse de `false` dit « rien n'a été écrit » : la case reprend la valeur ENREGISTRÉE. */
  onValidate: (v: string) => void | Promise<boolean>;
  /** `true` = champ ENCADRÉ pleine largeur (dessin des qualifications) ; sinon texte éditable en ligne. */
  encadre?: boolean;
}) {
  const [saisie, setSaisie] = useState(valeur);
  useEffect(() => { setSaisie(valeur); }, [valeur]);
  /**
   * Échap a été pressé : la sortie qui suit ABANDONNE au lieu d'écrire.
   * ⚠️ PAS L'ÉTAT : `blur()` part DANS le même événement que la touche, avant que `setSaisie(valeur)` soit rendu — la
   *    sortie lisait encore la frappe, et Échap écrivait la correction qu'il prétendait abandonner.
   */
  const abandon = useRef(false);

  return (
    <input
      value={saisie}
      onChange={(e) => setSaisie(e.target.value)}
      onBlur={() => {
        if (abandon.current) { abandon.current = false; return; }
        const ecrit = onValidate(saisie);
        // ⚠️ UNE SAISIE NON ÉCRITE REPREND LA VALEUR DU SERVEUR : refusée ou vide, rien n'a changé côté serveur, la
        //    relecture ne déclenche pas l'effet ci-dessus, et la case garderait ce qui n'a pas été enregistré.
        if (ecrit) void ecrit.then((ok) => { if (!ok) setSaisie(valeur); });
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        // Échap ABANDONNE la correction : sans lui, on ne peut plus sortir du champ sans écrire.
        if (e.key === 'Escape') { abandon.current = true; setSaisie(valeur); e.currentTarget.blur(); }
      }}
      title={heading}
      aria-label={nom ?? heading}
      {...(encadre ? {} : { size: Math.max(4, saisie.length) })}
      style={encadre
        // Le champ du dessin : bordé, haut, texte gras — on vient l'éditer, il doit s'annoncer.
        ? {
          // ⚠️ `font: 'inherit'` D'ABORD : c'est une propriété RACCOURCIE — déclarée après, elle
          //    réinitialise le poids et la taille posés au-dessus. Le champ restait en 400 alors que
          //    la ligne demandait 700, et rien à l'écran ne disait pourquoi.
          font: 'inherit',
          width: '100%', boxSizing: 'border-box', height: 44, padding: '0 14px',
          border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)',
          color: 'var(--ink-900)', fontSize: '14px', fontWeight: 700, outline: 'none',
        }
        : {
          font: 'inherit', // idem : le raccourci d'abord, les précisions ensuite
          border: '1px solid transparent', borderRadius: 'var(--r-xs)', background: 'transparent',
          color: 'var(--ink-800)', fontSize: '12.5px', fontWeight: 600, outline: 'none',
          padding: '0 3px', minWidth: 0,
        }}
    />
  );
}


/**
 * LES MARQUES SANS FOURNISSEUR — elles existent au catalogue, l'écran ne les cache pas.
 * ⚠️ Les taire ferait croire que tout est rattaché, et le champ « Marque » d'une fiche article
 * offrirait des valeurs qu'aucun fournisseur ne revendique, sans qu'on sache d'où elles viennent.
 */
function CarteOrphelines({ marques }: { marques: MarqueDuReferentiel[] }) {
  return (
    <div style={{
      background: 'var(--paper)', border: '1px dashed var(--ink-300)', borderRadius: 'var(--r-md)',
      padding: '15px 18px',
    }}>
      <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--ink-900)' }}>
        
        {t('Marques sans fournisseur')}
      </div>
      <p style={{ margin: '3px 0 10px', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
        
        {t('Elles alimentent déjà les fiches articles. Pour en rattacher une, tapez son nom dans la case « Nouvelle marque… » du fournisseur voulu : l’écran la reconnaîtra et proposera de la rattacher plutôt que d’en créer une seconde. Elle héritera alors de son coef corner.')}
      </p>
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
        {marques.map((b) => (
          <span key={b.code} style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, height: 30, padding: '0 11px',
            border: '1px solid var(--ink-200)', borderRadius: 'var(--r-pill)', background: 'var(--paper)',
            fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-800)',
          }}>
            {b.label}
            {b.models > 0 && (
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: '10.5px', fontWeight: 400, color: 'var(--ink-400)',
              }}>{t('{n} article', { n: b.models })}</span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * LES QUALIFICATIONS CLIENT — le vocabulaire que le magasin se donne pour nommer ses clients
 * (« VIP », « Comité d'entreprise », « Professionnel de santé »…), et qui alimente le champ
 * « Qualification » de la fiche client.
 *
 * ⚠️ ON NE SUPPRIME PAS, ON DÉSACTIVE : une qualification retirée du choix reste LISIBLE sur les
 *    fiches qui la portent. La supprimer rendrait muettes des fiches qualifiées des années plus tôt —
 *    c'est pourquoi l'écran montre l'USAGE de chaque ligne, et pas un bouton « Supprimer ».
 * ⚠️ Le CODE porte l'identité (il est écrit dans chaque fiche) ; le LIBELLÉ se corrige sur place,
 *    comme les marques — renommer n'a jamais à toucher une seule fiche.
 */
/**
 * LES SIX COULEURS PROPOSÉES — celles du dessin de référence (maquette Réglages › Clients).
 * ⚠️ UNE PALETTE FERMÉE, et c'est un choix : un sélecteur libre produirait des étiquettes illisibles
 * (jaune sur blanc) et autant de nuances que d'opérateurs. Six repères se distinguent d'un coup
 * d'œil ; au-delà, la couleur cesse d'être un repère.
 */
/**
 * ⚠️ LA PALETTE VIENT DE LA CHARTE (`@orgazone/ui-tokens`), elle n'est PAS écrite ici : le
 * back-office la propose, la caisse affiche ce qui en sort. Deux copies auraient divergé au premier
 * ajustement de teinte, et rien ne l'aurait signalé.
 */
const COULEURS_QUALIF = LABEL_COLORS;

/**
 * Le nom de chaque pastille de teinte — une phrase ENTIÈRE par couleur, jamais un mot glissé dans une phrase (l'accord
 * de la couleur change d'une langue à l'autre). ⚠️ FERMÉE PAR LE TYPE : une teinte ajoutée au socle réclame son nom ici ;
 * sans lui, six pastilles s'annonçaient toutes « Teinter « VIP » ».
 */
const NOM_TEINTE: Record<(typeof COULEURS_QUALIF)[number], Translatable> = {
  '#16A34A': key('Teinter « {label} » en vert'),
  '#2563EB': key('Teinter « {label} » en bleu'),
  '#7C5CFF': key('Teinter « {label} » en violet'),
  '#D97706': key('Teinter « {label} » en ambre'),
  '#DC2626': key('Teinter « {label} » en rouge'),
  '#6B7280': key('Teinter « {label} » en gris'),
};

/**
 * ⚠️ LA LARGEUR DU BLOC DE TEINTES SE CALCULE, elle ne se devine pas — et surtout elle est FIGÉE.
 * À largeur libre, le bloc se dimensionne sur son contenu et le champ voisin bouge d'une ligne à
 * l'autre : les champs cessent d'être alignés, pour une raison invisible à l'écran.
 */
const TEINTE_TAILLE = LABEL_COLOR_SWATCH.size;
const TEINTE_ESPACE = LABEL_COLOR_SWATCH.gap;
const TEINTES_LARGEUR = COULEURS_QUALIF.length * TEINTE_TAILLE + (COULEURS_QUALIF.length - 1) * TEINTE_ESPACE;

/**
 * La largeur de la COLONNE d'aperçu — figée, alors que le badge qu'elle contient garde sa taille
 * naturelle.
 * ⚠️ POURQUOI FIGER LA COLONNE : « Donateur » et « Vendeur » n'occupent pas la même place. Si la
 * colonne suivait son contenu, le champ de texte — qui absorbe tout le reste de la ligne —
 * changerait de largeur d'une ligne à l'autre, et les champs cesseraient d'être alignés pour une
 * raison invisible à l'écran.
 * ⚠️ ET POURQUOI NE PAS FIGER LE BADGE : un badge étiré sur toute sa colonne ne ressemble plus à ce
 * qu'il montre — l'aperçu doit être la pastille TELLE QU'ELLE apparaîtra sur une fiche.
 */
const APERCU_LARGEUR = 132;

function ApercuQualif({ label, color }: { label: string; color: string | null }) {
  return (
    // ⚠️ LA PRIMITIVE `Badge`, PAS UNE PASTILLE MAISON : elle accepte des couleurs LIBRES (`colors`)
    //    en plus de ses tons de rôle — exactement le cas d'une étiquette choisie par l'exploitant.
    //    Refaire la capsule à la main aurait fait diverger rayon, hauteur et graisse du reste du
    //    back-office au premier ajustement de la charte.
    <Badge
      size="sm"
      dot
      colors={color
        ? { bg: `color-mix(in oklab, ${color} 14%, var(--paper))`, fg: `color-mix(in oklab, ${color} 78%, var(--ink-900))` }
        : { bg: 'var(--ink-100)', fg: 'var(--ink-600)' }}
      // ⚠️ TAILLE NATURELLE : la pastille épouse son libellé, comme partout ailleurs. C'est sa
      //    COLONNE qui est figée (voir le conteneur), pas elle — un badge étiré sur toute la
      //    largeur ne ressemblerait plus à ce qu'il montre.
      style={{ maxWidth: '100%', overflow: 'hidden' }}
      srLabel={t('Aperçu de l’étiquette {label}', { label })}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
    </Badge>
  );
}

function OngletQualifications() {
  const [rows, setRows] = useState<ClientQualification[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [nouvelle, setNouvelle] = useState('');
  const [ecriture, setEcriture] = useState(false);
  /**
   * Les lignes dont l'écriture VIENT d'aboutir — marque éphémère.
   * ⚠️ SANS ELLE, L'ÉCRAN SE TAIT QUAND IL RÉUSSIT (défaut trouvé sur question utilisateur
   * « une modification se sauvegarde comment ? on n'a pas de bouton ? »). Renommer, teinter et
   * désactiver partaient bien en base, mais rien ne le disait : seul l'ÉCHEC parlait. Un écran qui
   * ne s'exprime qu'en cas d'échec apprend à l'opérateur que le silence veut dire « rien ne s'est
   * passé » — l'inverse exact de la vérité.
   */
  const [enregistrees, setEnregistrees] = useState<Record<string, number>>({});
  const marquerEnregistre = useCallback((code: string) => {
    setEnregistrees((x) => ({ ...x, [code]: Date.now() }));
    // La marque s'efface seule : elle confirme un geste, elle ne décrit pas un état durable.
    setTimeout(() => setEnregistrees((x) => {
      const { [code]: _, ...reste } = x;
      return reste;
    }), 1800);
  }, []);

  const relire = useCallback(() => {
    api.qualifications()
      .then((r) => { setRows(r.rows); setFailure(null); })
      .catch((e: unknown) => setFailure(e instanceof Error ? e.message : String(e)));
  }, []);
  useEffect(() => { relire(); }, [relire]);

  /**
   * Ajouter — le libellé SAISI sert aussi de code.
   * ⚠️ Le dessin ne demande qu'UN champ : le code est donc dérivé du libellé (c'est l'identité, et
   * `normalizeCode` la rend stable côté serveur). Renommer ensuite ne change que le libellé, donc
   * ne détache aucune fiche — c'est ce que promet la note du bas.
   */
  const ajouter = async () => {
    const nom = nouvelle.trim();
    if (!nom) { showToast(t('Donnez un nom à l’étiquette — c’est lui que les fiches porteront.'), 'err'); return; }
    setEcriture(true);
    try {
      await api.saveQualification({
        code: nom, label: nom, sortOrder: rows?.length ?? 0,
        color: COULEURS_QUALIF[(rows?.length ?? 0) % COULEURS_QUALIF.length],
        // « VIP » quand « vip » existe : refusé par le serveur, qui nomme l'étiquette existante.
        create: true,
      });
      setNouvelle('');
      showToast(t('Étiquette « {nom} » ajoutée.', { nom }));
      relire();
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally { setEcriture(false); }
  };

  /** Renomme — rend `true` quand le libellé est écrit (la case reprend sinon le libellé enregistré). */
  const renommer = async (q: ClientQualification, nom: string) => {
    try {
      await api.saveQualification({ code: q.code, label: nom, sortOrder: q.sortOrder, isActive: q.isActive });
      marquerEnregistre(q.code);
      relire();
      return true;
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
      return false;
    }
  };

  const teinter = async (q: ClientQualification, color: string) => {
    try {
      await api.saveQualification({ code: q.code, label: q.label, sortOrder: q.sortOrder, isActive: q.isActive, color });
      marquerEnregistre(q.code);
      relire();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err'); }
  };

  /** Supprimer — la CASCADE est annoncée AVANT, et le résultat NOMMÉ après. */
  const remove = async (q: ClientQualification) => {
    const ok = await confirm({
      title: t('Supprimer « {label} » ?', { label: q.label }),
      message: q.usage > 0
        ? t('L’étiquette sera RETIRÉE DE {n} FICHE client. Elle ne la portera plus, et les caisses l’apprendront à la prochaine synchro. C’est irréversible.', { n: q.usage })
        : t('Aucune fiche ne la porte : la retirer du référentiel n’a aucun autre effet.'),
      confirmLabel: t('Supprimer'), tone: 'danger',
    });
    if (!ok) return;
    try {
      const r = await api.deleteQualification(q.code);
      showToast(r.fichesTouchees > 0
        ? t('« {label} » supprimée — retirée de {n} fiche.', { label: q.label, n: r.fichesTouchees })
        : t('« {label} » supprimée.', { label: q.label }));
      relire();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err'); }
  };

  const basculer = async (q: ClientQualification) => {
    // Désactiver une étiquette PORTÉE se confirme : les fiches la gardent, mais on ne l'attribuera plus.
    if (q.isActive && q.usage > 0) {
      const ok = await confirm({
        title: t('Désactiver « {label} » ?', { label: q.label }),
        message: t('{n} fiche porte cette étiquette. Elle la GARDE et continuera de l’afficher — l’étiquette ne sera simplement plus proposée aux nouvelles fiches.', { n: q.usage }),
        confirmLabel: t('Désactiver'), tone: 'info',
      });
      if (!ok) return;
    }
    try {
      await api.setQualificationActive(q.code, !q.isActive);
      marquerEnregistre(q.code);
      relire();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e), 'err'); }
  };

  if (failure) {
    return (
      <div role="alert" style={{ padding: 20, fontSize: '13px', color: 'var(--danger-ink)' }}>
        
        {t('Le référentiel n’a pas pu être lu : {failure}', { failure })}
        <button type="button" className="btn-ghost" onClick={relire} style={{ marginLeft: 12 }}>{t('Réessayer')}</button>
      </div>
    );
  }
  if (rows === null) return <div style={{ padding: 20, fontSize: '13px', color: 'var(--ink-500)' }}>{t('Lecture du référentiel…')}</div>;

  return (
    /* ══ CARTE — porté du dessin de référence (maquette Réglages › Clients) ══════════════════════
       Une CARTE blanche : tuile d'icône, titre, sous-titre, compteur à droite ; puis une ligne PAR
       étiquette, chacune dans SON cadre ; puis la ligne d'ajout ; puis la note.
       ⚠️ Chaque ligne est un CADRE, pas une rangée libre : les six teintes et l'aperçu se lisent
       comme appartenant à l'étiquette qu'ils décrivent, pas comme une colonne du tableau. */
    <div style={{
      border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)',
      padding: 20, display: 'flex', flexDirection: 'column', gap: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <span style={{ width: 44, height: 44, borderRadius: 'var(--r-sm)', background: 'var(--teal-100)', color: 'var(--teal-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name="tag" size={21} />
        </span>
        <span style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
          <span style={{ display: 'block', fontSize: '15px', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Qualifications clients')}</span>
          {/* Le sous-titre porte l'encre d'accent du dessin : il DÉSIGNE deux écrans, il ne commente pas. */}
          <span style={{ display: 'block', fontSize: '12.5px', color: 'var(--blue-fg)', marginTop: 3, lineHeight: 1.45 }}>
            
            {t('Liste proposée dans les fiches clients — en vente comme dans l’écran Clients.')}
          </span>
          {/* ⚠️ IL N'Y A PAS DE BOUTON « ENREGISTRER », ET ÇA SE DIT. Rien n'est mis en attente :
              chaque geste part en base sur-le-champ. Sans cette phrase, l'absence de bouton se lit
              « mes modifications ne sont pas sauvegardées » — c'est exactement la question posée. */}
          <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--ink-400)', marginTop: 3 }}>
            
            {t('Chaque modification est enregistrée immédiatement — il n’y a rien à valider.')}
          </span>
        </span>
        <span style={{ minWidth: 28, height: 28, padding: '0 9px', borderRadius: 'var(--r-sm)', background: 'var(--ink-100)', color: 'var(--ink-600)', fontSize: '12.5px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
          {rows.length}
        </span>
      </div>

      {rows.length === 0 ? (
        <EmptyState icon="users" title={t('Aucune qualification')}
          note={t('Ce magasin n’a pas encore défini son vocabulaire. Ajoutez-en une ci-dessous — ou créez-la directement depuis une fiche client, elle rejoindra cette liste.')} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((q) => (
            <div key={q.code} style={{
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', padding: 10,
              background: 'var(--paper)', opacity: q.isActive ? 1 : 0.62,
            }}>
              {/* Le LIBELLÉ, en grand : c'est lui qu'on vient corriger. Le code, invisible, porte
                  l'identité — renommer ne détache donc aucune fiche. */}
              {/* Tout le reste — et comme tous les voisins ont une largeur FIGÉE, ce reste est le
                  MÊME sur chaque ligne : les champs s'alignent au pixel. */}
              <span style={{ flex: '1 1 240px', minWidth: 0 }}>
                <ChampTexte valeur={q.label} heading={t('Renommer — les fiches déjà qualifiées gardent l’étiquette')}
                  nom={t('Libellé de la qualification {v}', { v: q.label })}
                  encadre
                  onValidate={(v) => {
                    const nom = v.trim();
                    if (nom === '') return Promise.resolve(false);
                    return nom === q.label ? undefined : renommer(q, nom);
                  }} />
              </span>

              {/* Les six teintes ; celle en cours porte un anneau sombre. */}
              <span style={{ display: 'inline-flex', gap: TEINTE_ESPACE, flexShrink: 0, width: TEINTES_LARGEUR }}>
                {COULEURS_QUALIF.map((c) => {
                  const choisie = q.color === c;
                  return (
                    <button key={c} type="button" onClick={() => void teinter(q, c)}
                      aria-label={t(NOM_TEINTE[c], { label: q.label })} aria-pressed={choisie} title={c}
                      style={{
                        width: TEINTE_TAILLE, height: TEINTE_TAILLE, flexShrink: 0,
                        borderRadius: '50%', background: c, cursor: 'pointer', padding: 0,
                        border: choisie ? '2px solid var(--paper)' : '2px solid transparent',
                        boxShadow: choisie ? '0 0 0 2px var(--ink-900)' : 'none',
                      }} />
                  );
                })}
              </span>

              {/* L'aperçu : l'étiquette telle qu'elle apparaîtra sur une fiche. Son infobulle porte
                  l'usage — le compte n'a pas de colonne dans le dessin, mais on ne le perd pas. */}
              {/* ⚠️ LA COLONNE est figée, PAS le badge : à largeur libre, l'aperçu prendrait la place
                  que son libellé occupe et le champ voisin, qui absorbe le reste, changerait de
                  largeur d'une ligne à l'autre. Le badge, lui, garde sa taille naturelle. */}
              <span
                title={q.usage > 0 ? t('{n} fiche porte cette étiquette', { n: q.usage }) : t('Aucune fiche ne la porte')}
                style={{ width: APERCU_LARGEUR, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
              >
                {enregistrees[q.code]
                  ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: '11.5px', fontWeight: 700, color: 'var(--teal-700)' }}>
                      <Icon name="check" size={13} />
                      
                      {t('Enregistré')}
                    </span>
                  )
                  : <ApercuQualif label={q.label} color={q.color} />}
              </span>

              {/* ⚠️ ÉCART ASSUMÉ AU DESSIN : l'œil (désactiver / réactiver) n'y figure pas. Le retirer
                  supprimerait le seul chemin vers un état que l'API sait produire — une étiquette
                  désactivée n'aurait alors plus aucun moyen de revenir. Consigné au LISEZ-MOI. */}
              <button type="button" onClick={() => void basculer(q)}
                aria-label={q.isActive ? t('Désactiver « {label} »', { label: q.label }) : t('Réactiver « {label} »', { label: q.label })}
                title={q.isActive ? t('Désactiver — la liste ne la propose plus, les fiches la gardent') : t('Réactiver')}
                style={{ width: 30, height: 30, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'transparent', color: 'var(--ink-400)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {/* ⚠️ Le registre n'a pas d'« œil barré » : l'état DÉSACTIVÉ se lit à l'opacité de
                    la ligne et à l'infobulle. Dessiner une 2ᵉ icône hors registre créerait un tracé
                    que personne ne retrouverait ailleurs. */}
                <Icon name="eye" size={16} />
              </button>

              <button type="button" onClick={() => void remove(q)} aria-label={t('Supprimer « {label} »', { label: q.label })}
                title={t('Supprimer — retire l’étiquette de toutes les fiches')}
                style={{ width: 30, height: 30, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'transparent', color: 'var(--danger-ink)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name="trash" size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={nouvelle} onChange={(e) => setNouvelle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void ajouter(); } }}
          placeholder={t('Nouvelle qualification (ex. Dépositaire)')} aria-label={t('Nouvelle qualification')}
          style={{ flex: '1 1 280px', minWidth: 0, height: 44, padding: '0 14px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '13.5px', boxSizing: 'border-box' }} />
        {/* Bouton en teinte PÂLE tant qu'il n'y a rien à ajouter — il n'invite pas à un geste sans effet. */}
        <button type="button" onClick={() => void ajouter()} aria-disabled={ecriture || !nouvelle.trim() || undefined}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, height: 44, padding: '0 20px',
            border: 'none', borderRadius: 'var(--r-sm)', fontSize: '13.5px', fontWeight: 600,
            cursor: nouvelle.trim() && !ecriture ? 'pointer' : 'default',
            background: nouvelle.trim() && !ecriture ? 'var(--teal-600)' : 'var(--teal-100)',
            color: nouvelle.trim() && !ecriture ? '#fff' : 'var(--teal-700)',
          }}>
          <Icon name="plus" size={15} />
          {ecriture ? t('Ajout…') : t('Ajouter')}
        </button>
      </div>

      {/* La note du dessin : chaque conséquence porte l'encre de ce qu'elle fait. */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '11.5px', color: 'var(--ink-500)', lineHeight: 1.65 }}>
        <span style={{ flexShrink: 0, marginTop: 2, display: 'inline-flex' }}><Icon name="info" size={14} /></span>
        <span>
          <Trans k='<1>Renommer</1> conserve les clients déjà qualifiés ; <2>supprimer retire</2> l’étiquette de toutes les fiches. En production, <3>la liste vient du back-office</3> (référentiel partagé entre les caisses) et les <4>affectations remontent à la synchro</4>.' parts={[(x) => <strong style={{ fontWeight: 600, color: 'var(--ink-600)' }}>{x}</strong>, (x) => <strong style={{ fontWeight: 600, color: 'var(--danger-ink)' }}>{x}</strong>, (x) => <strong style={{ fontWeight: 600, color: 'var(--teal-700)' }}>{x}</strong>, (x) => <strong style={{ fontWeight: 600, color: 'var(--teal-700)' }}>{x}</strong>]} /></span>
      </div>
    </div>
  );
}
